import { de, fr, enGB, it } from "date-fns/locale";
import { format } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import { getLang } from "./i18n";
export const months = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];

const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
/** Years up to this value were entered in shorthand notation, e.g. "26" for 2026. */
const MAX_SHORTHAND_YEAR = 99;
const MIN_TWO_DIGIT_YEAR = 10;
const SHORTHAND_YEAR_CENTURY = 2000;
/** First two digits of the years a date field accepts (2000 - 2100). */
const FOUR_DIGIT_YEAR_PREFIXES = [20, 21];
const MIN_DATE_FIELD_YEAR = 2000;
const MAX_DATE_FIELD_YEAR = 2100;

/**
 * Whether a date field holds a complete date it accepts (2000 - 2100). While the user types, a date
 * field reports intermediate values such as "" or "0020-10-08".
 */
export const isCompleteDateFieldValue = (dateValue: string | undefined): boolean => {
    const isoDateMatch = ISO_DATE_PATTERN.exec(dateValue ?? "");

    if (!isoDateMatch) {
        return false;
    }

    const year = Number(isoDateMatch[1]);

    return year >= MIN_DATE_FIELD_YEAR && year <= MAX_DATE_FIELD_YEAR && !isNaN(Date.parse(isoDateMatch[0]));
};

/**
 * Completes a year that the user entered in shorthand notation in a date field.
 * A date field reports the year of "30.1.26" as "0026-01-30", which becomes "2026-01-30".
 * Dates with a complete year stay untouched.
 */
export const completeShorthandYear = (dateValue: string): string => {
    const isoDateMatch = ISO_DATE_PATTERN.exec(dateValue);

    if (!isoDateMatch) {
        return dateValue;
    }

    const [, year, month, day] = isoDateMatch;

    if (Number(year) > MAX_SHORTHAND_YEAR) {
        return dateValue;
    }

    return `${SHORTHAND_YEAR_CENTURY + Number(year)}-${month}-${day}`;
};

/**
 * Completes a shorthand year while the user is still typing in a date field.
 * A year with a single digit and the start of a four digit year ("20", "21") stay untouched:
 * a date field discards the digits typed next as soon as its value is set, so "2026" would
 * end up as "2006". Those years are completed once the field is left.
 */
export const completeShorthandYearWhileTyping = (dateValue: string): string => {
    const isoDateMatch = ISO_DATE_PATTERN.exec(dateValue);

    if (!isoDateMatch) {
        return dateValue;
    }

    const year = Number(isoDateMatch[1]);

    if (year < MIN_TWO_DIGIT_YEAR || FOUR_DIGIT_YEAR_PREFIXES.includes(year)) {
        return dateValue;
    }

    return completeShorthandYear(dateValue);
};

export const dateFormat = (date: any, formatStr = "PP") => {
    let locale: Locale = de;
    if (getLang().indexOf("de") !== -1) locale = de;
    if (getLang().indexOf("en") !== -1) locale = enGB;
    if (getLang().indexOf("fr") !== -1) locale = fr;
    if (getLang().indexOf("it") !== -1) locale = it;
    return format(date, formatStr, { locale });
};

export const getDate = (timestamp: string) => {
    if (timestamp) {
        return dateFormat(new Date(timestamp), "dd.MM.yyyy");
    }
};

export const getDateAndTime = (timestamp: string) => {
    if (timestamp) {
        return dateFormat(new Date(timestamp), "dd.MM.yyyy HH:mm");
    }
};

export const getDateWithLongMonthAndTime = (timestamp: string) => {
    if (timestamp) {
        return dateFormat(new Date(timestamp), "dd MMMM yyyy - HH:mm");
    }
};

export const getDateWithLongMonth = (timestamp: string) => {
    if (timestamp) {
        return dateFormat(new Date(timestamp), "dd. MMMM yyyy");
    }
};

const TIME_ZONE_SUFFIX_PATTERN = /(Z|[+-]\d{2}:\d{2})$/;

// The database stores timestamps like "updated" in UTC without a time zone. A value that already carries one
// ("Z" or an offset) is taken as it is, otherwise appending "Z" would make it an invalid date.
export const parseUtcTimestamp = (timestamp: string): Date =>
    new Date(TIME_ZONE_SUFFIX_PATTERN.test(timestamp) ? timestamp : `${timestamp}Z`);

// For timestamps the database stores in UTC without a time zone, like "updated". Shows the date in Zurich.
export const getDateWithLongMonthOfUtcTimestamp = (timestamp: string) => {
    if (timestamp) {
        return dateFormat(utcToZonedTime(parseUtcTimestamp(timestamp), "Europe/Zurich"), "dd. MMMM yyyy");
    }
};

export const getDateWithMonthAndYear = (timestamp: string) => {
    if (timestamp) {
        return dateFormat(new Date(timestamp), "MMMM yyyy");
    }
};

export const getDateForHasuraQuery = (timestamp: string) => {
    if (timestamp) {
        return dateFormat(new Date(timestamp), "yyyy-MM-dd");
    }
};

export const getTime = (timestamp: string) => {
    if (timestamp) {
        return dateFormat(new Date(timestamp), "HH:mm");
    }
    return "";
};
